// Comparing two instances of one process. Everything here is pure: the pooled map already draws
// the union of the pair (see graphWithInstances), so what the comparison still owes is the part
// pooling loses — the *order* each instance ran its steps in, and which readings differ.
//
// The map answers "where do the two routes part"; this module answers "in what order, and by how
// much". Neither is derivable from the other, which is why the pair gets its own panel body.
import type { ProcessGraphNode, ProcessInstance, ProcessStep } from "./processGraphModel";
import { caseElapsedSeconds, transitionWaitSeconds } from "./processGraphModel";

/** Which of the two instances a reading belongs to. `a` is the one compare was started from. */
export type CompareSide = "a" | "b";

/**
 * The colour a side is drawn in wherever it covers area — a route stroke, a step fill, a column
 * tint. Both the map and the panel read it from here, so a route on the graph and a row in the
 * list can never end up saying different things. It is deliberately a muted mid-tone: comparing
 * should read as two runs picked out of the same workspace, not as a different workspace.
 */
export const compareSideColor = (side: CompareSide): string =>
  side === "a" ? "var(--color-compare-a)" : "var(--color-compare-b)";

/**
 * The same hue pulled dark enough to be read as text or as a small solid mark on paper. The area
 * colour above is too light to carry either; using it for both is what would force it back up to
 * signal strength and undo the softening.
 */
export const compareSideInk = (side: CompareSide): string =>
  side === "a" ? "var(--color-compare-a-ink)" : "var(--color-compare-b-ink)";

/**
 * The side's colour as a fill behind text: a light tint, because a step box carries a name and two
 * readings and the line colour would fight them. It is still the side's own hue, so a box and the
 * route leading into it read as one thing.
 */
export const compareSideFill = (side: CompareSide): string =>
  `color-mix(in srgb, ${compareSideColor(side)} 30%, var(--color-canvas))`;

/**
 * The two instances under comparison, in the order they were chosen. Ordering is not cosmetic:
 * every delta on screen reads "b relative to a", so swapping the sides flips their sign.
 */
/** The side's ink as a text class, for a reading that names one run inside prose. */
export const compareSideTextClass = (side: CompareSide): string =>
  side === "a" ? "text-compare-a-ink" : "text-compare-b-ink";

export type ComparePair = { aId: string; bId: string; aIds?: string[]; bIds?: string[] };

export const compareSideIds = (pair: ComparePair, side: CompareSide): string[] =>
  side === "a" ? (pair.aIds ?? [pair.aId]) : (pair.bIds ?? [pair.bId]);

/** An absent step contributes zero to a group's average, rather than shrinking its denominator. */
export const formatCompareCount = (value: number): string =>
  value > 0 && value < 0.1 ? "<0.1" : Number(value.toFixed(1)).toLocaleString();

export const sameComparePair = (a: ComparePair | null, b: ComparePair | null): boolean =>
  a === b || (a !== null && b !== null && a.aId === b.aId && a.bId === b.bId);

/** The pair with one side replaced, refusing a pick that would compare an instance with itself. */
export const withCompareSide = (
  pair: ComparePair,
  side: CompareSide,
  instanceId: string,
): ComparePair => {
  if (side === "a") {
    return instanceId === pair.bId
      ? { aId: instanceId, bId: pair.aId }
      : { ...pair, aId: instanceId };
  }
  return instanceId === pair.aId
    ? { aId: pair.bId, bId: instanceId }
    : { ...pair, bId: instanceId };
};

// ---------------------------------------------------------------------------
// Per-instance readings

/**
 * Work time, elapsed time, and waits answer different questions. Keep them separate so a case
 * with more active work does not read as a case that merely waited longer between steps.
 */
export type CompareInstanceStats = {
  /** Active work on the case's steps, excluding waits. */
  totalSeconds: number;
  /** Observed wall-clock span, including waits, or null without endpoint timestamps. */
  elapsedSeconds: number | null;
  /**
   * Summed recorded gaps between consecutive steps, or null when any gap lacks timestamps.
   * Null is not zero: "nothing waited" and "we cannot tell" must not read alike.
   */
  waitingSeconds: number | null;
  stepCount: number;
  /** Steps beyond the first visit to each — the run's repeats, wherever they fell. */
  reworkCount: number;
};

export const compareInstanceStats = (instance: ProcessInstance): CompareInstanceStats => {
  const steps = instance.steps;
  const distinct = new Set(steps.map(step => step.nodeId));
  let waitingSeconds: number | null = 0;
  for (let index = 0; index + 1 < steps.length; index += 1) {
    const gap = transitionWaitSeconds(steps[index], steps[index + 1]);
    if (gap === null) {
      waitingSeconds = null;
      break;
    }
    waitingSeconds += gap;
  }
  return {
    totalSeconds: instance.source.avgDuration,
    elapsedSeconds: caseElapsedSeconds(instance),
    waitingSeconds,
    stepCount: steps.length,
    reworkCount: steps.length - distinct.size,
  };
};

/** Group readings use every run as the denominator, including runs without a given step. */
export const compareGroupStats = (runs: ProcessInstance[]): CompareInstanceStats => {
  const stats = runs.map(compareInstanceStats);
  const mean = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / runs.length;
  const knownMean = (values: (number | null)[]) =>
    values.some(value => value === null) ? null : mean(values as number[]);
  return {
    totalSeconds: mean(stats.map(stat => stat.totalSeconds)),
    elapsedSeconds: knownMean(stats.map(stat => stat.elapsedSeconds)),
    waitingSeconds: knownMean(stats.map(stat => stat.waitingSeconds)),
    stepCount: mean(stats.map(stat => stat.stepCount)),
    reworkCount: mean(stats.map(stat => stat.reworkCount)),
  };
};

// ---------------------------------------------------------------------------
// Step alignment

/**
 * One line of the divergence list. A `shared` row is a step both runs took at the same point in
 * their common backbone; an `only` row is one side's detour. The rows are in run order, which is
 * the reading the pooled map cannot give: on the map a step visited three times is one box.
 */
export type CompareAlignmentRow =
  | { kind: "shared"; nodeId: string; aIndex: number; bIndex: number }
  | { kind: "only"; side: CompareSide; nodeId: string; index: number };

/**
 * Above this many DP cells the alignment is abandoned for a positional pairing. Two instances of
 * one process are normally tens of steps, so this only ever catches a pathological analysis — and
 * a panel that renders something coarse beats one that locks the tab up computing the ideal answer.
 */
const MAX_ALIGNMENT_CELLS = 250_000;

/** Steps paired by position, for the runs too long to align properly. */
const positionalAlignment = (
  aSteps: ProcessStep[],
  bSteps: ProcessStep[],
): CompareAlignmentRow[] => {
  const rows: CompareAlignmentRow[] = [];
  const shared = Math.min(aSteps.length, bSteps.length);
  for (let index = 0; index < shared; index += 1) {
    rows.push(
      aSteps[index].nodeId === bSteps[index].nodeId
        ? { kind: "shared", nodeId: aSteps[index].nodeId, aIndex: index, bIndex: index }
        : { kind: "only", side: "a", nodeId: aSteps[index].nodeId, index },
    );
    if (aSteps[index].nodeId !== bSteps[index].nodeId) {
      rows.push({ kind: "only", side: "b", nodeId: bSteps[index].nodeId, index });
    }
  }
  for (let index = shared; index < aSteps.length; index += 1) {
    rows.push({ kind: "only", side: "a", nodeId: aSteps[index].nodeId, index });
  }
  for (let index = shared; index < bSteps.length; index += 1) {
    rows.push({ kind: "only", side: "b", nodeId: bSteps[index].nodeId, index });
  }
  return rows;
};

/**
 * The two step sequences read as a diff: longest common subsequence for the backbone, each side's
 * remaining steps hanging off it in run order. Between two shared anchors A's detour is listed
 * before B's, so a divergence reads as one hunk — this side did that, the other did this — rather
 * than as two interleaved sequences the eye has to unpick.
 */
export const alignInstanceSteps = (
  aSteps: ProcessStep[],
  bSteps: ProcessStep[],
): CompareAlignmentRow[] => {
  const n = aSteps.length;
  const m = bSteps.length;
  if (n === 0 || m === 0 || n * m > MAX_ALIGNMENT_CELLS) {
    return positionalAlignment(aSteps, bSteps);
  }

  // lcs[i][j] = length of the longest common subsequence of a[i..] and b[j..]. Filling from the
  // end lets the backtrack below run forward, which is the order the rows are emitted in.
  const width = m + 1;
  const lcs = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        aSteps[i].nodeId === bSteps[j].nodeId
          ? lcs[(i + 1) * width + (j + 1)] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + (j + 1)]);
    }
  }

  const rows: CompareAlignmentRow[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aSteps[i].nodeId === bSteps[j].nodeId) {
      rows.push({ kind: "shared", nodeId: aSteps[i].nodeId, aIndex: i, bIndex: j });
      i += 1;
      j += 1;
      continue;
    }
    // Take from whichever side can still be caught up with; A's detour is emitted first, so a
    // run of unmatched steps on both sides comes out grouped by side rather than interleaved.
    const aFirst = lcs[(i + 1) * width + j] >= lcs[i * width + (j + 1)];
    if (aFirst) {
      rows.push({ kind: "only", side: "a", nodeId: aSteps[i].nodeId, index: i });
      i += 1;
    } else {
      rows.push({ kind: "only", side: "b", nodeId: bSteps[j].nodeId, index: j });
      j += 1;
    }
  }
  for (; i < n; i += 1) rows.push({ kind: "only", side: "a", nodeId: aSteps[i].nodeId, index: i });
  for (; j < m; j += 1) rows.push({ kind: "only", side: "b", nodeId: bSteps[j].nodeId, index: j });
  return rows;
};

// ---------------------------------------------------------------------------
// Per-node readings

/** One side's visits to one pooled step. */
type CompareNodeSideStats = { count: number; seconds: number };

type CompareNodeStats = { a: CompareNodeSideStats; b: CompareNodeSideStats };

const EMPTY_SIDE: CompareNodeSideStats = { count: 0, seconds: 0 };

/**
 * How each side used one box on the map. This is what turns the pooled subtitle from "4x · 41s"
 * — a number belonging to neither run — into two readings that can be compared, and a side with a
 * zero count is exactly the "only this run goes here" case.
 */
export const compareNodeStats = (node: ProcessGraphNode, pair: ComparePair): CompareNodeStats => {
  let a = EMPTY_SIDE;
  let b = EMPTY_SIDE;
  const aIds = new Set(compareSideIds(pair, "a"));
  const bIds = new Set(compareSideIds(pair, "b"));
  for (const occurrence of node.instances) {
    if (aIds.has(occurrence.instanceId)) {
      a = { count: a.count + 1, seconds: a.seconds + occurrence.duration };
    }
    if (bIds.has(occurrence.instanceId)) {
      b = { count: b.count + 1, seconds: b.seconds + occurrence.duration };
    }
  }
  return {
    a: { count: a.count / aIds.size, seconds: a.seconds / aIds.size },
    b: { count: b.count / bIds.size, seconds: b.seconds / bIds.size },
  };
};

/** Which sides visit a node at all — the map's per-box identity bar. */
export const compareNodeSides = (stats: CompareNodeStats): CompareSide[] => [
  ...(stats.a.count > 0 ? (["a"] as const) : []),
  ...(stats.b.count > 0 ? (["b"] as const) : []),
];
